import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { erApiFeil, erNettfeil } from '../api/feil.ts';
import { useHypoteseOvergang, type AdjustRequest } from '../api/useHypoteseBehandling.ts';
import { useHypoteseMedNummer, type Hypothesis } from '../api/useHypotheses.ts';
import { useMe } from '../api/useMe.ts';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { Ikon } from '../status/Ikon.tsx';
import { Fot, Tekstknapp, Varsel } from './deler.tsx';
import { Felt } from './HDetalj.tsx';
import { Skjelettrader } from './HListe.tsx';
import { nummerTekst } from './Kvittering.tsx';
import { endretDel, fornavn, genitiv, hvem, navnAv, sitat, stor, ventendeJustering } from './lesning.ts';
import { kortDato, kortDatoAv, tall } from './setning.ts';

/**
 * «Juster før start» (P6b) — konseptsidens skjerm 6b er fasit for formen (DD-71), wireframens
 * DetaljJuster for tilstandene og reglene. Egen side under detaljens adresse (/h/…/142/juster),
 * ikke et ark. Produkteier 2026-10-08: redigering gjennom dagens felt, ett per ledd (kontrakten
 * tar hele formen); de to versjonene side om side med det endrede leddet markert (`<del>`/`<ins>`
 * i setningen, strek og ramme — aldri farge alene) som resultatet; «Hvilke ledd, og hvorfor» er
 * grunnen; «Forslag til [beslutningstakerens] svar» er `proposedReply`, valgfritt.
 *
 * Etter konseptet (koordinator 2026-10-08): versjonene står rett under overskriften og oppdateres
 * mens leddene skrives; feltene under dem. Forslaget er et generert utkast (endret ledd + grunnen)
 * til personen selv har rørt feltet, og sendes bare når det ikke er tomt. Foten har knappen
 * først, setningen ved siden av og «Avbryt» helt til høyre.
 *
 * Foten har ÉTT primærvalg (DD-58): «Send til [beslutningstakeren]», sperret med grunnen til et
 * ledd er endret og grunnen er lang nok. «Avbryt» (og Esc) går tilbake uten å lagre noe — det
 * som ble skrevet, forsvinner med siden.
 */

/** Leddene som kan justeres (`HypothesisAdjustRequest` er hele formen; dette er setningens ledd). */
export type JusterFelt = 'change' | 'audience' | 'effect' | 'metricName' | 'sourceText' | 'threshold' | 'testEndsOn';
export const JUSTER_LEDD: Array<[JusterFelt, string]> = [
  ['change', 'Endringen'],
  ['audience', 'For hvem'],
  ['effect', 'Effekten'],
  ['metricName', 'Måltallet'],
  ['sourceText', 'Kilden'],
  ['threshold', 'Terskelen'],
  ['testEndsOn', 'Innen'],
];

const GRUNN_MIN = 20;
const MAKS = 500;

type Form = Hypothesis['revisions'][number]['form'];

const verdiAv = (h: Hypothesis, f: JusterFelt): string => (f === 'threshold' ? (h.threshold != null ? tall(h.threshold) : '') : (h[f] ?? ''));

/** «46» eller «46,5» → tallet; noe annet → NaN (422 threshold_not_numeric). */
const somTall = (v: string): number => (/^-?\d+([.,]\d+)?$/.test(v.trim()) ? Number(v.trim().replace(',', '.')) : Number.NaN);

/** En brikke i setningen: bindeord (`fw`) eller et ledd med verdien slik den står i setningen. */
interface Brikke {
  tekst: string;
  felt?: JusterFelt;
}

/** Setningen med tjenerens ord (`setning.ts`, `HypothesisForms.Sentence`), som brikker per ledd. */
function brikker(f: Partial<Form>): Brikke[] {
  const enhet = f.unit?.trim() ?? '';
  const medEnhet = (n: number) => (enhet ? `${tall(n)} ${enhet}` : tall(n));
  const pil = f.baselineState === 'maalt' && f.baselineValue != null ? `${medEnhet(f.baselineValue)} → ` : '';
  const retning = f.direction === 'hoyst' ? 'høyst' : 'minst';
  return [
    { tekst: 'Vi tror at' },
    { tekst: f.change ?? '—', felt: 'change' },
    { tekst: 'for' },
    { tekst: f.audience ?? '—', felt: 'audience' },
    { tekst: 'gir' },
    { tekst: f.effect ?? '—', felt: 'effect' },
    { tekst: ', målt ved' },
    { tekst: f.metricName ?? '—', felt: 'metricName' },
    { tekst: 'fra' },
    { tekst: f.sourceText ?? '—', felt: 'sourceText' },
    { tekst: '. Vi har rett hvis' },
    { tekst: f.threshold != null && !Number.isNaN(f.threshold) ? `${pil}${retning} ${medEnhet(f.threshold)}` : '—', felt: 'threshold' },
    { tekst: 'innen' },
    { tekst: f.testEndsOn ? kortDato(f.testEndsOn) : '—', felt: 'testEndsOn' },
    { tekst: '.' },
  ];
}

/**
 * Én versjon av setningen. Et endret ledd viser bare den delen som endret seg — strøket i Før,
 * innrammet i Nå (`endretDel`); resten av setningen står likt i begge.
 */
function Versjonssetning({ fra, til, side }: { fra: Brikke[]; til: Brikke[]; side: 'foer' | 'naa' }) {
  const deler: ReactNode[] = [];
  fra.forEach((b, i) => {
    const mot = til[i]!;
    const egen = side === 'foer' ? b : mot;
    const mellomrom = i > 0 && !/^[,.]/.test(egen.tekst) ? ' ' : '';
    if (!b.felt) {
      deler.push(
        <span key={i} className="hj-bindeord">
          {mellomrom}
          {egen.tekst}
        </span>,
      );
      return;
    }
    if (b.tekst === mot.tekst) {
      deler.push(<span key={i}>{`${mellomrom}${egen.tekst}`}</span>);
      return;
    }
    const d = endretDel(b.tekst, mot.tekst);
    deler.push(
      <span key={i}>
        {mellomrom}
        {d.foer ? `${d.foer} ` : ''}
        {side === 'foer' ? <del className="hj-endret">{d.ut}</del> : <ins className="hj-endret">{d.inn}</ins>}
        {d.etter ? ` ${d.etter}` : ''}
      </span>,
    );
  });
  return <p className="hj-setning">{deler}</p>;
}

export function JusterSide({ nr, base }: { nr: number; base: string }) {
  const smal = useSmal();
  const naviger = useNavigate();
  const me = useMe();
  const detalj = useHypoteseMedNummer(nr);
  const overgang = useHypoteseOvergang();
  const megId = me.data?.id;
  const h = detalj.data ?? null;
  const tilbake = `${base}/${nr}`;

  const [ledd, setLedd] = useState<Partial<Record<JusterFelt, string>>>({});
  const [grunn, setGrunn] = useState('');
  const [forslag, setForslag] = useState('');
  // Utkastet følger leddene og grunnen til personen selv skriver i feltet — da er teksten hennes.
  const [forslagRoert, setForslagRoert] = useState(false);
  const [feil, setFeil] = useState<{ code: string; tekst: string } | null>(null);

  // Esc er «Avbryt» (wireframens fot): tilbake til detaljen, ingenting lagres.
  const avbrytRef = useRef(() => void naviger(tilbake));
  avbrytRef.current = () => void naviger(tilbake);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      avbrytRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const verdier = useMemo(() => (h ? Object.fromEntries(JUSTER_LEDD.map(([f]) => [f, ledd[f] ?? verdiAv(h, f)])) : {}) as Record<JusterFelt, string>, [h, ledd]);
  const endret = h ? JUSTER_LEDD.filter(([f]) => verdier[f].trim() !== verdiAv(h, f).trim()) : [];
  const terskelFeil = verdier.threshold !== undefined && verdier.threshold.trim() !== '' && Number.isNaN(somTall(verdier.threshold));

  const stripe = (tittel: string) =>
    smal ? (
      <header className="h-stripe" data-testid="hj-stripe">
        <Link to={tilbake} className="panel-stripe-tilbake" aria-label={`Tilbake til ${nummerTekst(nr)}`}>
          <Ikon navn="chevron-back-outline" />
        </Link>
        <h1 className="h-stripe-tittel">{tittel}</h1>
      </header>
    ) : null;

  const melding = (tittel: string, tekst: string) => (
    <div className="h-flate">
      {stripe(tittel)}
      <div className="h-rull">
        <div className="h-innhold h-innhold-les">
          {smal ? null : <h1 className="panel-h1">{tittel}</h1>}
          <p className="hb-dempet">{tekst}</p>
          <div>
            <Link to={tilbake} className="panel-knapp-sekundaer">
              {`Tilbake til ${nummerTekst(nr)}`}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );

  if (detalj.isPending || me.isPending) {
    return (
      <div className="h-flate">
        {stripe('Juster før start')}
        <Skjelettrader etikett="Henter hypotesen" />
      </div>
    );
  }
  // K2: en hypotese som ikke finnes, sier det samme som en du ikke har tilgang til.
  if (detalj.isError || h === null) return melding(`Fant ikke ${nummerTekst(nr)}`, 'Den finnes ikke, eller den er ikke din å se. Sjekk nummeret.');

  const eier = megId !== undefined && h.owner?.id === megId;
  const vurderer = megId !== undefined && h.reviewer?.id === megId;
  // Tjeneren vokter (403 not_owner, 409 wording_locked, 409 revision_unapproved); dette er speilet som forklarer først.
  if (!eier && !vurderer) {
    return melding('Justering er eierens og vurdererens', `${stor(navnAv(h.owner))} (eier) eller ${navnAv(h.reviewer)} (vurderer) foreslår en justering; ${navnAv(h.decider)} godkjenner den.`);
  }
  const venter = ventendeJustering(h);
  if (h.status !== 'valgt' || h.startedAt || venter) {
    return melding(
      'Justering er ikke åpen',
      venter ? `En justering venter alt på ${hvem(h.decider, megId, 'deg')}. Den må godkjennes eller avslås før en ny kan foreslås.` : 'En justering finnes bare i Valgt, før testen starter. Etter start er ordlyden låst.',
    );
  }

  const gjeldende = h.approvedRevision ?? h.currentRevision;
  const gjeldendeRev = h.revisions.find((r) => r.revision === gjeldende);
  const ny = h.currentRevision + 1;
  const beslutter = navnAv(h.decider);
  const beslutterF = fornavn(h.decider);
  const eierOrd = eier ? 'du' : fornavn(h.owner);
  const innsender = navnAv(h.submittedBy);

  const foerForm: Partial<Form> = h as Partial<Form>;
  const naaForm: Partial<Form> = { ...foerForm };
  for (const [f] of endret) {
    if (f === 'threshold') naaForm.threshold = somTall(verdier.threshold);
    else naaForm[f] = verdier[f].trim();
  }
  const fra = brikker(foerForm);
  const til = brikker(naaForm);
  const endringer = fra
    .map((b, i) => [b, til[i]!] as const)
    .filter(([b, t]) => b.felt && b.tekst !== t.tekst)
    .map(([b, t]) => {
      const d = endretDel(b.tekst, t.tekst);
      return `${sitat(d.ut)} ble ${sitat(d.inn)}`;
    });
  const utkast = endringer.length > 0 ? `Hypotesen din ble justert før start: ${endringer.join('; ')}.${grunn.trim() ? ` Grunn: ${grunn.trim()}` : ''}`.slice(0, MAKS) : '';
  const forslagTekst = forslagRoert ? forslag : utkast;

  const antall = endret.length === 0 ? 'Ingen ledd er endret ennå.' : endret.length === 1 ? 'Ett ledd er endret.' : `${endret.length} ledd er endret.`;
  const sperre =
    endret.length === 0
      ? 'Endre minst ett ledd først.'
      : terskelFeil
        ? 'Terskelen må være et tall.'
        : grunn.trim().length < GRUNN_MIN
          ? `Skriv hvilke ledd og hvorfor — minst ${GRUNN_MIN} tegn.`
          : null;

  async function send() {
    if (sperre || !h) return;
    const body: AdjustRequest = { reason: grunn.trim() };
    for (const [f] of endret) {
      if (f === 'threshold') body.threshold = somTall(verdier.threshold);
      else body[f] = verdier[f].trim();
    }
    if (forslagTekst.trim()) body.proposedReply = forslagTekst.trim();
    setFeil(null);
    try {
      await overgang.mutateAsync({ id: h.id, overgang: { handling: 'adjust', body } });
      void naviger(tilbake, { state: { kvittering: `Justeringen er sendt til ${beslutter}.` } });
    } catch (e) {
      if (erApiFeil(e)) setFeil({ code: e.code, tekst: erNettfeil(e) ? 'Ikke sendt — ingen nett. Det du skrev står. Prøv igjen når nettet er tilbake.' : e.brukertekst });
      else setFeil({ code: 'ukjent', tekst: 'Noe gikk galt. Det du skrev står. Prøv igjen.' });
    }
  }

  const versjonFoer = `Versjon ${gjeldende}${gjeldendeRev ? ` · ${hvem(gjeldendeRev.by, megId, 'deg')}, ${kortDatoAv(gjeldendeRev.at)}` : ''}`;
  const versjonNaa = `Versjon ${ny} · justert av deg`;

  return (
    <div className="h-flate hj-side" data-testid="hj-side">
      {stripe('Juster før start')}
      <div className="h-rull">
        <div className="h-innhold hj-innhold">
          {smal ? null : (
            <Link to={tilbake} className="panel-knapp-lenke hj-tilbake" aria-label={`Tilbake til ${nummerTekst(nr)}: ${h.title}`}>
              <Ikon navn="chevron-back-outline" />
              {h.title}
            </Link>
          )}
          <div className="hj-hode">
            {smal ? null : <h1 className="panel-h1">Juster før start</h1>}
            <p className="hb-dempet">{`${antall} ${beslutter} må godkjenne før ${eierOrd} kan starte; ${innsender} får beskjed når det er gjort.`}</p>
          </div>
          {feil && feil.code !== 'threshold_not_numeric' ? (
            <Varsel rolle="alert">{`Justeringen ble ikke sendt. ${feil.tekst}`}</Varsel>
          ) : null}
          <div className="hj-versjoner" data-testid="hj-versjoner">
            <div className="hj-versjon" role="group" aria-label={`Før · ${versjonFoer.charAt(0).toLowerCase()}${versjonFoer.slice(1)}`}>
              <span className="hj-versjon-etikett">
                <Ikon navn="document-text-outline" />
                {versjonFoer}
              </span>
              <Versjonssetning fra={fra} til={til} side="foer" />
            </div>
            <div className="hj-versjon hj-versjon-naa" role="group" aria-label={`Nå · ${versjonNaa.charAt(0).toLowerCase()}${versjonNaa.slice(1)}`}>
              <span className="hj-versjon-etikett hj-versjon-etikett-naa">
                <Ikon navn="create-outline" />
                {versjonNaa}
              </span>
              <Versjonssetning fra={fra} til={til} side="naa" />
            </div>
          </div>
          <div className="hj-ledd" role="group" aria-label="Leddene">
            {JUSTER_LEDD.map(([f, navn]) => (
              <Felt key={f} id={`hj-${f}`} etikett={navn} grunn={f === 'threshold' && (terskelFeil || feil?.code === 'threshold_not_numeric') ? 'Terskelen må være et tall.' : null}>
                <input
                  id={`hj-${f}`}
                  type={f === 'testEndsOn' ? 'date' : 'text'}
                  inputMode={f === 'threshold' ? 'decimal' : undefined}
                  className={f === 'testEndsOn' ? 'h-input h-input-dato' : 'h-input'}
                  value={verdier[f]}
                  aria-invalid={(f === 'threshold' && terskelFeil) || undefined}
                  onChange={(e) => setLedd((l) => ({ ...l, [f]: e.target.value }))}
                />
              </Felt>
            ))}
          </div>
          <Felt id="hj-grunn" etikett={`Hvilke ledd, og hvorfor · ${GRUNN_MIN}–${MAKS} tegn`} teller={`${grunn.length} / ${MAKS}`}>
            <textarea id="hj-grunn" className="h-input h-tekstfelt" rows={2} maxLength={MAKS} value={grunn} onChange={(e) => setGrunn(e.target.value)} />
          </Felt>
          <Felt id="hj-forslag" etikett={`Forslag til ${genitiv(beslutterF)} svar til ${innsender} · valgfritt`} teller={`${forslagTekst.length} / ${MAKS}`}>
            <textarea
              id="hj-forslag"
              className="h-input h-tekstfelt"
              rows={3}
              maxLength={MAKS}
              value={forslagTekst}
              onChange={(e) => {
                setForslagRoert(true);
                setForslag(e.target.value);
              }}
              aria-describedby="hj-forslag-hjelp"
            />
          </Felt>
          <p id="hj-forslag-hjelp" className="hb-dempet">
            {`Et utgangspunkt for ${genitiv(beslutterF)} svar — ${beslutterF} må skrive det om før det sendes til ${fornavn(h.submittedBy)}.`}
          </p>
        </div>
      </div>
      <Fot
        primarFoerst
        venstre={<span className="hb-fot-tekst">{`Er versjon ${ny} — gjelder når ${beslutterF} godkjenner.`}</span>}
        hoyre={
          <Tekstknapp onClick={() => avbrytRef.current()} testid="hj-avbryt">
            Avbryt
          </Tekstknapp>
        }
        grunn={sperre}
        primar={{ label: `Send til ${beslutter}`, onClick: () => void send(), opptatt: overgang.isPending, ikon: 'send-outline', testid: 'hj-primar' }}
      />
    </div>
  );
}
